// src/components/calendar/EventListView.jsx
import { useEffect, useRef, useState } from "react";
import { Reorder, useDragControls } from "framer-motion";
import parseStringDate from "../../utils/parseStringDate";
import EventCard from "./EventCard";

// ==========================================
// SORT HELPERS
// ==========================================

/**
 * Sort events by their manual sortOrder (small numbers = top), then by start
 * time as a tiebreaker. Events with no sortOrder fall through to a very
 * large value so they end up at the bottom of the day.
 */
const sortByManualOrder = (a, b) => {
  const ao =
    typeof a.sortOrder === "number" ? a.sortOrder : Number.MAX_SAFE_INTEGER;
  const bo =
    typeof b.sortOrder === "number" ? b.sortOrder : Number.MAX_SAFE_INTEGER;
  if (ao !== bo) return ao - bo;
  return parseStringDate(a.start) - parseStringDate(b.start);
};

/**
 * ListEventItem
 *
 * One reorderable event row in the Today's / Selected-date sections. Wraps
 * EventCard in a Reorder.Item and prepends a dedicated drag handle so the
 * card itself can still be clicked to open the editor.
 */
const ListEventItem = ({ event, showDate, onEdit, onDragEnd, driversById }) => {
  const controls = useDragControls();

  return (
    <Reorder.Item
      value={event}
      dragListener={false}
      dragControls={controls}
      onDragEnd={onDragEnd}
      className="flex items-stretch gap-2"
    >
      {/* Drag handle — only this element initiates a drag */}
      <button
        type="button"
        onPointerDown={(e) => controls.start(e)}
        onClick={(e) => e.stopPropagation()}
        className="flex items-center px-2 text-gray-400 hover:text-gray-700 cursor-grab active:cursor-grabbing touch-none"
        title="Drag to reorder"
        aria-label="Drag to reorder"
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M7 4a1 1 0 110-2 1 1 0 010 2zm0 5a1 1 0 110-2 1 1 0 010 2zm0 5a1 1 0 110-2 1 1 0 010 2zm0 5a1 1 0 110-2 1 1 0 010 2zm6-15a1 1 0 110-2 1 1 0 010 2zm0 5a1 1 0 110-2 1 1 0 010 2zm0 5a1 1 0 110-2 1 1 0 010 2zm0 5a1 1 0 110-2 1 1 0 010 2z" />
        </svg>
      </button>

      <div className="flex-1 min-w-0">
        <EventCard
          event={event}
          showDate={showDate}
          onClick={onEdit}
          driversById={driversById}
        />
      </div>
    </Reorder.Item>
  );
};

/**
 * EventListView Component
 *
 * Displays events in a card-based list format with Today's and selected-date
 * sections. Each section supports drag-and-drop reordering — the new order
 * is persisted to Firestore via the parent's onReorderDay callback.
 * When searching, displays all search results in a single list (no reorder).
 */
const EventListView = ({
  events,
  onEditEvent,
  searchQuery = "",
  onReorderDay,
  driversById = {},
}) => {
  // ==========================================
  // STATE MANAGEMENT
  // ==========================================

  // Initialize with tomorrow's date
  const [selectedDate, setSelectedDate] = useState(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow;
  });

  // Local copies of each list so Reorder.Group can mutate them during a drag
  // without waiting for a parent re-render.
  const [localTodaysEvents, setLocalTodaysEvents] = useState([]);
  const [localSelectedDateEvents, setLocalSelectedDateEvents] = useState([]);

  // Track which list has unsaved drag changes so we know which to persist
  // when the user drags-and-then-navigates-away.
  const todaysDirtyRef = useRef(false);
  const selectedDirtyRef = useRef(false);

  // ==========================================
  // EVENT FILTERING UTILITIES
  // ==========================================

  /**
   * Get today's events (from upstream events list, sorted by manual order)
   */
  const getTodaysEvents = () => {
    const today = new Date();
    const todayStr = today.toDateString();

    return events
      .filter((event) => {
        const eventDate = parseStringDate(event.start);
        return eventDate.toDateString() === todayStr;
      })
      .sort(sortByManualOrder);
  };

  /**
   * Get events for the currently selected date, sorted by manual order
   */
  const getSelectedDateEvents = () => {
    const selectedDateStr = selectedDate.toDateString();

    return events
      .filter((event) => {
        const eventDate = parseStringDate(event.start);
        return eventDate.toDateString() === selectedDateStr;
      })
      .sort(sortByManualOrder);
  };

  // ==========================================
  // LOCAL STATE SYNC
  // ==========================================

  // Re-sync from upstream when events change OR the selected date changes.
  // We do NOT replace local state if the user has an unsaved drag —
  // otherwise mid-drag re-renders from the parent would yank the row back.
  useEffect(() => {
    if (!todaysDirtyRef.current) {
      setLocalTodaysEvents(getTodaysEvents());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events]);

  useEffect(() => {
    if (!selectedDirtyRef.current) {
      setLocalSelectedDateEvents(getSelectedDateEvents());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, selectedDate]);

  // ==========================================
  // NAVIGATION HANDLERS
  // ==========================================

  /**
   * Navigate to previous day. Flushes any pending reorder on the
   * selected-date list before changing the date.
   */
  const handlePreviousDay = () => {
    flushSelectedReorder();
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
  };

  /**
   * Navigate to next day. Flushes any pending reorder on the
   * selected-date list before changing the date.
   */
  const handleNextDay = () => {
    flushSelectedReorder();
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    setSelectedDate(newDate);
  };

  // ==========================================
  // REORDER HANDLERS
  // ==========================================

  const handleReorderToday = (newOrder) => {
    setLocalTodaysEvents(newOrder);
    todaysDirtyRef.current = true;
  };

  const handleReorderSelected = (newOrder) => {
    setLocalSelectedDateEvents(newOrder);
    selectedDirtyRef.current = true;
  };

  const flushTodaysReorder = () => {
    if (todaysDirtyRef.current && onReorderDay) {
      onReorderDay(localTodaysEvents);
      todaysDirtyRef.current = false;
    }
  };

  const flushSelectedReorder = () => {
    if (selectedDirtyRef.current && onReorderDay) {
      onReorderDay(localSelectedDateEvents);
      selectedDirtyRef.current = false;
    }
  };

  // Wrap onEditEvent so we flush any pending reorder before opening the
  // editor modal — otherwise the modal closes onto stale data.
  const handleEditFromToday = (event) => {
    flushTodaysReorder();
    onEditEvent(event);
  };
  const handleEditFromSelected = (event) => {
    flushSelectedReorder();
    onEditEvent(event);
  };

  // Flush both on unmount as a safety net (e.g. user switches view mode)
  useEffect(() => {
    return () => {
      flushTodaysReorder();
      flushSelectedReorder();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ==========================================
  // RENDER
  // ==========================================

  // If searching, show all results in a single column (no reorder)
  if (searchQuery.trim()) {
    // Sort events by date
    const sortedEvents = [...events].sort(
      (a, b) => parseStringDate(a.start) - parseStringDate(b.start)
    );

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Section Header */}
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center">
            <svg
              className="w-5 h-5 sm:w-6 sm:h-6 mr-2 text-indigo-600 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <span className="truncate">Search Results</span>
          </h2>
          <p className="text-xs sm:text-sm text-gray-600 mt-1">
            Found {events.length} event{events.length !== 1 ? "s" : ""} matching
            "{searchQuery}"
          </p>
        </div>

        {/* Events Content */}
        <div className="p-4 sm:p-6">
          {sortedEvents.length === 0 ? (
            /* Empty State */
            <div className="text-center py-8">
              <svg
                className="mx-auto h-12 w-12 text-gray-400 mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <p className="text-gray-500 mb-2">
                No events found matching "{searchQuery}"
              </p>
              <p className="text-gray-400 text-sm">
                Try a different search term
              </p>
            </div>
          ) : (
            /* Event Cards */
            <div className="space-y-3">
              {sortedEvents.map((event) => (
                <EventCard
                  key={event.id}
                  event={event}
                  showDate={true}
                  onClick={() => onEditEvent(event)}
                  driversById={driversById}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Normal view (no search)
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
      {/* Today's Events Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Section Header */}
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center">
            <svg
              className="w-5 h-5 sm:w-6 sm:h-6 mr-2 text-indigo-600 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span className="truncate">Today's Events</span>
          </h2>
          <p className="text-xs sm:text-sm text-gray-600 mt-1">
            {new Date().toLocaleDateString("en-GB", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
        </div>

        {/* Events Content */}
        <div className="p-4 sm:p-6">
          {localTodaysEvents.length === 0 ? (
            /* Empty State */
            <div className="text-center py-8">
              <svg
                className="mx-auto h-12 w-12 text-gray-400 mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <p className="text-gray-500 mb-2">
                No events scheduled for today
              </p>
              <button
                onClick={() => onEditEvent()}
                className="text-indigo-600 hover:text-indigo-800 text-sm font-medium transition-colors duration-200"
              >
                Create your first event
              </button>
            </div>
          ) : (
            /* Draggable Event Cards */
            <Reorder.Group
              axis="y"
              values={localTodaysEvents}
              onReorder={handleReorderToday}
              className="space-y-3"
            >
              {localTodaysEvents.map((event) => (
                <ListEventItem
                  key={event.id}
                  event={event}
                  showDate={false}
                  onEdit={() => handleEditFromToday(event)}
                  onDragEnd={flushTodaysReorder}
                  driversById={driversById}
                />
              ))}
            </Reorder.Group>
          )}
        </div>
      </div>

      {/* Selected Date Events Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Section Header */}
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center">
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6 mr-2 text-green-600 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
                <span className="truncate">Events</span>
              </h2>
              <p className="text-xs sm:text-sm text-gray-600 mt-1 truncate">
                {selectedDate.toLocaleDateString("en-GB", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            </div>

            {/* Navigation Buttons - Larger for mobile */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={handlePreviousDay}
                className="p-3 sm:p-2.5 rounded-lg border-2 border-gray-300 hover:bg-gray-50 active:bg-gray-100 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 active:scale-95"
                title="Previous Day"
                aria-label="Previous Day"
              >
                <svg
                  className="w-5 h-5 sm:w-5 sm:h-5 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <button
                onClick={handleNextDay}
                className="p-3 sm:p-2.5 rounded-lg border-2 border-gray-300 hover:bg-gray-50 active:bg-gray-100 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 active:scale-95"
                title="Next Day"
                aria-label="Next Day"
              >
                <svg
                  className="w-5 h-5 sm:w-5 sm:h-5 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Events Content */}
        <div className="p-4 sm:p-6">
          {localSelectedDateEvents.length === 0 ? (
            /* Empty State */
            <div className="text-center py-8">
              <svg
                className="mx-auto h-12 w-12 text-gray-400 mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <p className="text-gray-500 mb-2">
                No events scheduled for this day
              </p>
              <button
                onClick={() => onEditEvent()}
                className="text-indigo-600 hover:text-indigo-800 text-sm font-medium transition-colors duration-200"
              >
                Create an event
              </button>
            </div>
          ) : (
            /* Draggable Event Cards */
            <Reorder.Group
              axis="y"
              values={localSelectedDateEvents}
              onReorder={handleReorderSelected}
              className="space-y-3"
            >
              {localSelectedDateEvents.map((event) => (
                <ListEventItem
                  key={event.id}
                  event={event}
                  showDate={false}
                  onEdit={() => handleEditFromSelected(event)}
                  onDragEnd={flushSelectedReorder}
                  driversById={driversById}
                />
              ))}
            </Reorder.Group>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventListView;
